/**
 * Generic descriptor-engine save bridge (#997 Story 6.4 — extracted from
 * `useRacksEngineSaveBridge` at behavioral parity; the 27 racks bridge tests are
 * the refactor oracle).
 *
 * Replaces a section's parent-owned edit-session hook for the persisted (Request
 * Detail) path once the section runs on `DescriptorEngineSection`. The engine
 * section is STEP-mounted, so its internal diff baseline re-bootstraps from
 * `editedData` after step navigation — an engine-side flush would diff
 * edited-vs-edited and silently drop the user's changes. Session continuity
 * therefore lives in `editedData[editedDataKey]` (written by the engine's
 * `applyEditedDataPatch` bridge), and this hook owns the save-time REST flush.
 *
 * Baseline semantics (all three matter — the 6-3 review-patch cluster):
 *
 * - **Held, not live.** Captured ONCE per edit session and frozen: a live query
 *   baseline would let a teammate's mid-session unit addition appear in the diff
 *   as a session-side deletion and be DELETEd on save.
 * - **Same chain as the display list.** Seed = restored snapshot > section query
 *   (settled, non-empty) > request-embed rows > legacy flat-unit synthesis. The
 *   persisted layout shows the embed when the query is empty/errored, so the
 *   flush must diff against the same rows — a query-error `[]` baseline silently
 *   skips every persisted-id update.
 * - **Reset to the flush result.** The flush's normalized rows (temp→real ids)
 *   become the new baseline AND are written back into `editedData[editedDataKey]`;
 *   otherwise a save that fails AFTER the flush retries with stale temp-id rows
 *   and delete-recreates the just-saved unit.
 *
 * The section side is gated on the screen's dirty flag — baseline and session
 * come from different lineages, so an untouched session must never produce a
 * spurious flush (content equality is additionally guarded by the section's
 * canonicalized content-key comparator, `config.hasChanges`).
 *
 * Editor-open operations delegate to the engine's registered
 * `RepeatableInstanceSaveHandle` (captured via `registerInstanceSaveHandle`);
 * each section wraps the generic handle below into its named handle contract so
 * every existing call site keeps working.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { offlineRequestsKeys } from './queries/useOfflineRequestsQuery';

import type { RepeatableInstanceSaveHandle } from '../components/repeatableSections/repeatableInstanceSaveHandle';

/** Minimal identity an engine-bridged instance carries (temp id negative, real id > 0). */
export interface RepeatableEngineBridgeInstance {
  id?: number;
}

/** The section-neutral save handle the generic bridge produces. */
export interface RepeatableEngineGenericSaveHandle {
  isEditorOpen: () => boolean;
  hasUnsavedEditor: () => boolean;
  commitOpenEditor: () => Promise<boolean>;
  saveUnsavedIfNeeded: () => Promise<boolean>;
  hasPendingChanges: () => boolean;
  /**
   * POST/PUT/DELETE/reorder the section — call from Save Changes. Resolves with
   * the temp→real created-id map when the flush created units (#997 Obs 6): the
   * caller remaps staged upload keys `base.unit.{temp}` → `base.unit.{real}`
   * BEFORE the file upload, because the flush's server-side attachment remap only
   * moves rows already in the DB.
   */
  flushPendingChanges: () => Promise<ReadonlyMap<number, number> | void>;
  /**
   * #1501 aggregate save: the pending (baseline, session) pair WITHOUT
   * executing anything — null when there is nothing to send. The aggregate
   * payload builder feeds these into the pure compute functions.
   */
  getPendingDiff: () => { baseline: unknown[]; session: unknown[] } | null;
  /**
   * #1501 aggregate save: absorb an externally-executed flush result (the
   * aggregate response's idMap-normalized rows) — the exact post-flush reset
   * flushPendingChanges performs: held baseline + editedData snap to the
   * normalized rows, list/detail queries invalidated, dirty cleared. Without
   * this a retry would diff stale temp ids and delete-recreate saved units.
   */
  absorbExternalFlushResult: (normalizedRows: unknown[]) => Promise<void>;
  /**
   * #1501 aggregate save, partial (207) case: swap applied temp ids for their
   * server ids in the SESSION only — baseline and dirty state stay untouched,
   * so the retry re-diffs honestly (applied creates stop re-sending; the
   * failed ops still do).
   */
  remapSessionIds: (idMap: ReadonlyMap<number, number>) => void;
  tryDismissEditor: () => Promise<boolean>;
  discardOpenEditor: () => void;
  resetSessionAfterScopeRemoval: () => void;
}

/**
 * #1501 aggregate save: the two generic-handle methods the aggregate save
 * orchestrator needs from every family wrapper, under uniform names.
 */
export interface AggregateSaveBridgeExtras {
  getPendingDiff: RepeatableEngineGenericSaveHandle['getPendingDiff'];
  absorbExternalFlushResult: RepeatableEngineGenericSaveHandle['absorbExternalFlushResult'];
  remapSessionIds: RepeatableEngineGenericSaveHandle['remapSessionIds'];
}

/** Minimal react-query result shape the bridge reads from a section's list query. */
export interface RepeatableEngineInstancesQueryResult<TInstance> {
  data?: TInstance[];
  isLoading: boolean;
  isError: boolean;
}

export interface UseRepeatableEngineSaveBridgeConfig<
  TInstance extends RepeatableEngineBridgeInstance,
> {
  requestId: string | undefined;
  isEditing: boolean;
  isOfflineRequest: boolean;
  /** Flat request fields (request + editedData merge) — legacy unit synthesis. */
  legacyRequestFields?: Record<string, unknown>;
  /** The request payload's PERSISTED instances embed (baseline fallback). */
  persistedEmbedInstances?: unknown;
  /** Live session list — `editedData[editedDataKey]` (engine bridge writes it). */
  editedInstances?: unknown;
  /** True when the section was touched this session (`dirtyFields.has(editedDataKey)`). */
  instancesDirty: boolean;
  applyEditedDataPatch?: (patch: Record<string, unknown>) => void;
  restoredBaseline?: TInstance[] | null;
  onDirtyChange?: (dirty: boolean) => void;

  // ---- Section bindings (the only per-section variance) ----
  /** editedData key + dirty key for this section, e.g. `smallUpsConfigurations`. */
  editedDataKey: string;
  /** The section's list query hook (called unconditionally — must be stable). */
  useInstancesQuery: (
    requestId: string,
    enabled: boolean,
  ) => RepeatableEngineInstancesQueryResult<TInstance>;
  /** Canonical content-key change detector (baseline vs session). */
  hasChanges: (baseline: TInstance[], session: TInstance[]) => boolean;
  /** The section's proven bespoke flush (returns temp→real normalized rows). */
  flush: (
    requestId: string,
    baseline: TInstance[],
    session: TInstance[],
    isOfflineRequest: boolean,
  ) => Promise<TInstance[]>;
  /** Post-flush ROW-query invalidation keys (list + grouped). */
  invalidateListKeys: (requestId: string) => Array<readonly unknown[]>;
  /** Legacy flat-unit synthesis when the query + embed are empty (optional). */
  buildLegacyFromRequestFields?: (
    fields: Record<string, unknown>,
    requestId: string,
  ) => TInstance | null;
}

export interface UseRepeatableEngineSaveBridgeResult<TInstance> {
  saveHandle: RepeatableEngineGenericSaveHandle | null;
  registerInstanceSaveHandle: (
    handle: RepeatableInstanceSaveHandle | null,
  ) => void;
  /** The held edit-session baseline (for draft-snapshot persistence). */
  sessionBaseline: TInstance[] | null;
}

export function useRepeatableEngineSaveBridge<
  TInstance extends RepeatableEngineBridgeInstance,
>({
  requestId,
  isEditing,
  isOfflineRequest,
  legacyRequestFields,
  persistedEmbedInstances,
  editedInstances,
  instancesDirty,
  applyEditedDataPatch,
  restoredBaseline,
  onDirtyChange,
  editedDataKey,
  useInstancesQuery,
  hasChanges,
  flush,
  invalidateListKeys,
  buildLegacyFromRequestFields,
}: UseRepeatableEngineSaveBridgeConfig<TInstance>): UseRepeatableEngineSaveBridgeResult<TInstance> {
  const queryClient = useQueryClient();
  const enabled = Boolean(requestId) && isEditing;
  const {
    data: persistedInstances = [],
    isLoading,
    isError,
  } = useInstancesQuery(requestId ?? '', enabled);

  const engineHandleRef = useRef<RepeatableInstanceSaveHandle | null>(null);
  const registerInstanceSaveHandle = useCallback(
    (handle: RepeatableInstanceSaveHandle | null) => {
      engineHandleRef.current = handle;
    },
    [],
  );

  // Held baseline — captured once per edit session, frozen against live query
  // refetches, reset only by the flush. Null until seedable (query unsettled, or
  // errored with no embed/legacy fallback), which keeps the saveHandle null so
  // the save flow surfaces its "changes are still loading" guard instead of
  // diffing against a wrong baseline.
  const [heldBaseline, setHeldBaseline] = useState<TInstance[] | null>(null);

  useEffect(() => {
    if (!enabled) {
      setHeldBaseline(null);
      return;
    }
    if (heldBaseline != null) {
      return;
    }
    if (restoredBaseline) {
      setHeldBaseline(restoredBaseline.map(unit => ({ ...unit })));
      return;
    }
    if (isLoading) {
      return;
    }
    if (!isError && persistedInstances.length > 0) {
      setHeldBaseline(persistedInstances.map(unit => ({ ...unit })));
      return;
    }
    // Query settled empty OR errored: fall back to the same rows the display
    // list shows (embed > legacy synthesis) so edits to displayed units are
    // never diffed against a baseline that lacks them.
    if (
      Array.isArray(persistedEmbedInstances) &&
      persistedEmbedInstances.length > 0
    ) {
      setHeldBaseline(
        (persistedEmbedInstances as TInstance[]).map(unit => ({ ...unit })),
      );
      return;
    }
    if (requestId && legacyRequestFields && buildLegacyFromRequestFields) {
      const legacyUnit = buildLegacyFromRequestFields(
        legacyRequestFields,
        requestId,
      );
      if (legacyUnit) {
        setHeldBaseline([legacyUnit]);
        return;
      }
    }
    if (!isError) {
      setHeldBaseline([]);
    }
    // Errored with no fallback rows: stay null (save blocked, edits preserved).
  }, [
    enabled,
    heldBaseline,
    restoredBaseline,
    isLoading,
    isError,
    persistedInstances,
    persistedEmbedInstances,
    requestId,
    legacyRequestFields,
    buildLegacyFromRequestFields,
  ]);

  const sessionInstances = useMemo(
    (): TInstance[] | null =>
      Array.isArray(editedInstances) ? (editedInstances as TInstance[]) : null,
    [editedInstances],
  );

  const hasPendingChanges = useCallback((): boolean => {
    if (!enabled || !instancesDirty || !sessionInstances) {
      return false;
    }
    if (!heldBaseline) {
      // Baseline not seedable yet with edits pending — report pending so the
      // save flow surfaces its "still loading" guard instead of silently
      // dropping the flush.
      return true;
    }
    return hasChanges(heldBaseline, sessionInstances);
  }, [enabled, instancesDirty, sessionInstances, heldBaseline, hasChanges]);

  useEffect(() => {
    onDirtyChange?.(hasPendingChanges());
  }, [hasPendingChanges, onDirtyChange]);

  const saveHandle = useMemo((): RepeatableEngineGenericSaveHandle | null => {
    if (!enabled || !requestId || heldBaseline == null) {
      return null;
    }
    return {
      isEditorOpen: () =>
        engineHandleRef.current?.isInstanceEditorOpen() ?? false,
      hasUnsavedEditor: () =>
        engineHandleRef.current?.hasUnsavedInstanceEditor() ?? false,
      commitOpenEditor: async () =>
        engineHandleRef.current?.commitOpenInstanceEditor() ?? false,
      saveUnsavedIfNeeded: async () => {
        const engine = engineHandleRef.current;
        if (
          !engine?.isInstanceEditorOpen() ||
          !engine.hasUnsavedInstanceEditor()
        ) {
          return true;
        }
        return engine.commitOpenInstanceEditor();
      },
      hasPendingChanges,
      flushPendingChanges: async () => {
        if (
          !sessionInstances ||
          !instancesDirty ||
          !hasChanges(heldBaseline, sessionInstances)
        ) {
          return;
        }
        const flushed = await flush(
          requestId,
          heldBaseline,
          sessionInstances,
          isOfflineRequest,
        );
        // Derive the temp→real created-id map from the flush's order-preserving
        // normalization (session[i] → flushed[i]). Returned to the caller so it
        // can remap staged upload keys BEFORE uploadFilesForRequest runs (#997
        // Obs 6): the flush's server-side attachment remap only moves rows
        // already in the DB, so a staged file keyed `base.unit.{temp}` would
        // otherwise upload into a bucket the saved unit's editor never reads.
        const createdIdMap = new Map<number, number>();
        sessionInstances.forEach((unit, index) => {
          const flushedId = flushed[index]?.id;
          if (unit.id != null && flushedId != null && unit.id !== flushedId) {
            createdIdMap.set(unit.id, flushedId);
          }
        });
        const snapshot = flushed.map(unit => ({ ...unit }));
        // Reset the held baseline AND the editedData session to the flush's
        // normalized rows — a retry after a later save-step failure must diff
        // saved-vs-saved (no-op), not stale-temp-ids-vs-refetched-truth.
        setHeldBaseline(snapshot);
        applyEditedDataPatch?.({ [editedDataKey]: snapshot });
        for (const queryKey of invalidateListKeys(requestId)) {
          await queryClient.invalidateQueries({ queryKey });
        }
        // Refresh the request payload's embedded instances too — the view-mode
        // list falls back to that embed when the row query is empty, so a stale
        // embed resurrects deleted units as ghosts (#997).
        await queryClient.invalidateQueries({
          queryKey: offlineRequestsKeys.detail(requestId),
        });
        onDirtyChange?.(false);
        return createdIdMap;
      },
      getPendingDiff: () => {
        if (
          !sessionInstances ||
          !instancesDirty ||
          !hasChanges(heldBaseline, sessionInstances)
        ) {
          return null;
        }
        return { baseline: heldBaseline, session: sessionInstances };
      },
      absorbExternalFlushResult: async (normalizedRows: unknown[]) => {
        const snapshot = (normalizedRows as TInstance[]).map(unit => ({
          ...unit,
        }));
        setHeldBaseline(snapshot);
        applyEditedDataPatch?.({ [editedDataKey]: snapshot });
        for (const queryKey of invalidateListKeys(requestId)) {
          await queryClient.invalidateQueries({ queryKey });
        }
        await queryClient.invalidateQueries({
          queryKey: offlineRequestsKeys.detail(requestId),
        });
        onDirtyChange?.(false);
      },
      remapSessionIds: (idMap: ReadonlyMap<number, number>) => {
        if (!sessionInstances || idMap.size === 0) {
          return;
        }
        const remapped = sessionInstances.map(unit =>
          unit.id != null && idMap.has(unit.id)
            ? { ...unit, id: idMap.get(unit.id)! }
            : unit,
        );
        applyEditedDataPatch?.({ [editedDataKey]: remapped });
      },
      tryDismissEditor: async () =>
        engineHandleRef.current?.tryDismissInstanceEditor() ?? true,
      discardOpenEditor: () => {
        engineHandleRef.current?.discardOpenInstanceEditor();
      },
      resetSessionAfterScopeRemoval: () => {
        // The screen strips `editedData[editedDataKey]` and prunes the dirty
        // flag on scope removal; the engine section unmounts with the removed
        // step. Drop the held baseline so a re-added step reseeds.
        setHeldBaseline(null);
        onDirtyChange?.(false);
      },
    };
  }, [
    enabled,
    requestId,
    heldBaseline,
    sessionInstances,
    instancesDirty,
    isOfflineRequest,
    hasPendingChanges,
    applyEditedDataPatch,
    queryClient,
    onDirtyChange,
    editedDataKey,
    hasChanges,
    flush,
    invalidateListKeys,
  ]);

  return {
    saveHandle,
    registerInstanceSaveHandle,
    sessionBaseline: heldBaseline,
  };
}
